// The phone's home, and the desk's rail: the toolbar, the large title, the
// status card, then the projects grouped by what each needs from you. Every
// row's menu offers Focus, the full plan and Delete (behind a confirm); a
// keyboard walks the rows with the arrows, F focuses, Delete asks. Over it
// all, until it is done or put away, the setup list (app/onboarding). In the
// tour, the tour's demo project is listed too (a stand-in, never in the
// store) and the setup list steps aside.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isComplete, type Project } from '@shared/plan';
import { groupedByStanding } from '@shared/standing';
import { Button, Card, IconButton, Menu, SectionHeader, Sheet, Topbar, type MenuAt, type MenuItem } from '@app/ui';
import { useProjects, useRuns, useStoreReady } from '@app/store/hooks';
import { buildingIds, failedIds, store } from '@app/store/store';
import { useNav } from '../../router';
import { InstallHint } from '../../shell/InstallHint';
import { SetupCard } from '../../onboarding/SetupCard';
import { useSetup } from '../../onboarding/setupStore';
import { useStandIn } from '../../onboarding/standIn';
import { focusedJob, setFocusedJob } from '../focus/focused';
import { ProjectRow } from './ProjectRow';
import { StatusPager } from './StatusPager';
import { nextIndex, rowAction } from './keys';
import './home.css';

export interface HomeListProps {
  /** The project open beside the list on a desk, highlighted in the rail. */
  selectedId?: string;
}

export function HomeList({ selectedId }: HomeListProps) {
  const nav = useNav();
  const ready = useStoreReady();
  const projects = useProjects();
  const runs = useRuns();
  const standIn = useStandIn();
  const tourProject = standIn?.project;
  const listed = useMemo(() => (tourProject ? [tourProject, ...projects] : projects), [tourProject, projects]);
  const groups = useMemo(() => groupedByStanding([...listed], buildingIds(runs), failedIds(runs)), [listed, runs]);
  const setup = useSetup();
  const [menu, setMenu] = useState<{ id: string; at: MenuAt } | null>(null);
  // The project the confirm asks about stays while the sheet closes, so the title never blinks.
  const [confirm, setConfirm] = useState<{ project: Project; open: boolean } | null>(null);
  const list = useRef<HTMLDivElement>(null);

  const open = useCallback((id: string) => nav.push(`/plan/${id}`), [nav]);
  const focusOn = useCallback((id: string) => { setFocusedJob(id); nav.push('/focus'); }, [nav]);
  const askDelete = useCallback((id: string) => { const p = store.get(id); if (p) setConfirm({ project: p, open: true }); }, []);
  const keep = () => setConfirm((c) => (c ? { ...c, open: false } : null));
  const remove = () => {
    const p = confirm?.project;
    keep();
    if (!p) return;
    if (focusedJob() === p.id) setFocusedJob(null);
    void store.apply(p.id, { kind: 'delete' });
  };

  const itemsFor = (id: string): MenuItem[] => {
    const p = store.get(id);
    const finished = !p || isComplete(p);
    return [
      ...(finished ? [] : [{ label: 'Focus on this', icon: 'target', kbd: 'F', onSelect: () => focusOn(id) } satisfies MenuItem]),
      { label: 'Open the full plan', icon: 'chev', kbd: '↩', onSelect: () => open(id) },
      { label: 'Delete project…', icon: 'trash', kbd: '⌫', danger: true, onSelect: () => askDelete(id) },
    ];
  };

  // Keys, for a desk: the arrows walk the rows (and enter the list from
  // rest on the home screen), F focuses the row, Delete asks. Enter is the
  // row button's own. A sheet, a menu or a field keeps its keys.
  const atHome = nav.path === '/';
  // The setup list is home's, like the hint: not the rail's beside an open plan, and not the tour's.
  // It has its own Home Screen item, so the hint waits until the list is gone.
  const setupUp = atHome && !standIn && !setup.dismissed && !setup.celebrated;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = list.current;
      if (!el || e.metaKey || e.ctrlKey || e.altKey || document.querySelector('dialog[open]')) return;
      const target = e.target as HTMLElement | null;
      if (target?.closest('input, textarea, select, [contenteditable="true"], [role="menu"]')) return;
      const rows = [...el.querySelectorAll<HTMLElement>('.row-main')];
      const active = document.activeElement;
      const current = rows.findIndex((r) => r === active);
      const atRest = !active || active === document.body;
      const entering = current < 0 && atRest && atHome && (e.key === 'ArrowDown' || e.key === 'ArrowUp');
      if (current < 0 && !entering) return;
      const n = nextIndex(rows.length, current, e.key);
      if (n !== undefined) { e.preventDefault(); rows[n]?.focus(); return; }
      if (current < 0) return;
      const id = rows[current]?.closest<HTMLElement>('[data-id]')?.dataset.id;
      const action = rowAction(e.key);
      if (!id || !action) return;
      e.preventDefault();
      if (action === 'delete') askDelete(id);
      else { const p = store.get(id); if (p && !isComplete(p)) focusOn(id); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [atHome, askDelete, focusOn]);

  const count = groups.reduce((n, g) => n + g.projects.length, 0);
  return (
    <div className="home">
      <Topbar
        leading={<IconButton icon="gear" label="Settings" onClick={() => nav.push('/settings')} />}
        trailing={<IconButton icon="plus" label="New project" tone="honey" onClick={() => nav.push('/new')} />}
      />
      <h1 className="large-title">Honey-Do</h1>
      {ready && (
        <div className="home-body">
          {setupUp && <SetupCard />}
          <StatusPager projects={projects} />
          {/* An invitation for the home screen, not for the rail beside an open plan. */}
          {atHome && !setupUp && <InstallHint />}
          <div ref={list}>
            {groups.map((g) => (
              <section key={g.standing} className="home-group">
                <SectionHeader count={g.projects.length}>{g.title}</SectionHeader>
                <Card>
                  {g.projects.map((p) => (
                    <ProjectRow key={p.id} project={p} standing={g.standing} run={runs.get(p.id)} selected={p.id === selectedId} onOpen={open} onMenu={(id, at) => setMenu({ id, at })} />
                  ))}
                </Card>
              </section>
            ))}
          </div>
          {count === 0 && (
            <div className="home-empty">
              <b>No projects yet</b>
              <p className="note">Tap + to snap a spot and get your first plan.</p>
            </div>
          )}
          {count > 0 && (
            <p className="home-keys note"><kbd>↑</kbd><kbd>↓</kbd> move · <kbd>↩</kbd> open · <kbd>F</kbd> focus · <kbd>⌫</kbd> delete</p>
          )}
        </div>
      )}
      <Menu items={menu ? itemsFor(menu.id) : []} at={menu?.at ?? null} onClose={() => setMenu(null)} label="Project actions" />
      <Sheet open={confirm?.open ?? false} onClose={keep} title={confirm ? `Delete ${confirm.project.title}?` : 'Delete'} className="home-confirm">
        <p className="note">This can’t be undone.</p>
        {/* "Keep it" comes first in the DOM, so the sheet's initial focus (and a reflexive Enter) is the safe answer; Delete is drawn on top. */}
        <div className="home-confirm-actions">
          <Button variant="ghost" wide onClick={keep}>Keep it</Button>
          <Button variant="quiet" wide className="danger" icon="trash" onClick={remove}>Delete</Button>
        </div>
      </Sheet>
    </div>
  );
}
